import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import React, { useState } from 'react'
import { Tooltip } from 'antd'
import { cellClass, Table, TableCell } from '../../../../shared/ui/table/Table'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { ITrademarkInfringementsFilters } from '../../model/types/TrademarkInfringementsFilters.interface'
import { selectTrademarkInfringementsFilters } from '../../model/trademarkInfringementsSlice'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import { TrademarkInfringementsReportGraph } from '../trademark-infringements-report-graph/TrademarkInfringementsReportGraph'
import { ComplaintStatusBadge } from '../../ui/ComplaintStatusBadge'
import { RejectionReasonLabel } from '../../ui/RejectionReasonLabel'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { TrademarkInfringementsReplyModal } from '../trademark-infringements-reply-modal/TrademarkInfringementsReplyModal'
import { IconCross } from '../../../../shared/components/icons/IconEnvelope'
import {
  useGetTmInfringementsHistoryQuery,
  useGetTmInfringementsSuccessReportQuery,
} from '../../../../app/api'

export const TrademarkInfringementsTableExpandRow = ({ record }: { record: any }) => {
  const { t } = useTranslation()
  const { project, tab: pageTab } = useAppSelector<ITrademarkInfringementsFilters>(
    selectTrademarkInfringementsFilters,
  )
  const [tab, setTab] = useState(pageTab !== 'removed' ? 'submission_history' : 'success_report')
  const dispatch = useAppDispatch()

  const { data: successReport, isLoading: successReportLoading } =
    useGetTmInfringementsSuccessReportQuery({
      projectId: project as unknown as number,
      infringementId: record.infringement_id,
    })
  const { data: history, isLoading: historyLoading } = useGetTmInfringementsHistoryQuery({
    projectId: project as unknown as number,
    infringementId: record.infringement_id,
  })

  const HEADERS = [
    { label: t('table.columns.searchEngine') },
    { label: t('table.columns.caseId') },
    { label: t('table.columns.ad'), width: '292px' },
    { label: t('table.columns.trademark') },
    { label: t('table.columns.complaintStatus') },
    { label: t('table.columns.actionedBy') },
    { label: t('table.columns.lastFound') },
    { label: t('table.columns.updated') },
  ]

  const tabs = [
    {
      text: t('pages.trademarkInfringements.successReport'),
      value: 'success_report',
      hidden: pageTab !== 'removed',
    },
    {
      text: t('pages.trademarkInfringements.submissionHistory'),
      value: 'submission_history',
      hidden: false,
    },
  ]

  if (successReportLoading || historyLoading) {
    return (
      <div className={'flex justify-center py-[16px]'}>
        <div className={'relative w-[30px]'}>
          <Preloader />
        </div>
      </div>
    )
  }

  return (
    <div className={'mt-[-16px]'}>
      <BpTabs
        active={tab}
        className={'pl-[16px]'}
        size={'md'}
        onToggle={(e) => {
          const value = tabs.find((value) => value.text === e)?.value

          if (value && value !== tab) {
            setTab(value)
          }
        }}
      >
        {tabs
          .filter(({ hidden }) => !hidden)
          .map(({ text, value }) => (
            <BpTab key={value} title={text} />
          ))}
      </BpTabs>
      <div className={'w-full overflow-hidden rounded-[16px] border border-[#E5E1EA]'}>
        {
          {
            success_report: (
              <div className={'p-[16px]'}>
                <TrademarkInfringementsReportGraph data={successReport?.data as any} />
              </div>
            ),
            submission_history: (
              <div className={'w-full'}>
                <Table headers={HEADERS} isEmpty={!(history?.data as any)?.length}>
                  {(history?.data as any)?.map((item: any, index: number) => (
                    <tr key={index} className={'relative'}>
                      <TableCell className={cellClass}>{item.search_engine}</TableCell>
                      <TableCell className={cellClass}>
                        {item.case_id || (
                          <Tooltip title={t('pages.trademarkInfringements.caseIdPendingTooltip')}>
                            <span className={'text-[13px] text-[#6684A7]'}>—</span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className={cellClass}>
                        <div
                          className={
                            'flex w-[260px] flex-col gap-[8px] rounded-[4px] border border-dashed border-[#E5E1EA] bg-white px-[8px] py-[4px]'
                          }
                        >
                          <div>
                            <p
                              className={'truncate text-[15px] leading-[24px]'}
                              dangerouslySetInnerHTML={{ __html: item.ad.title }}
                            />
                          </div>
                          <div>
                            <p className={'text-[14px]'}>
                              <span className={'text-[#8E7CA7]'}>
                                {t('pages.trademarkInfringements.screenshots')}:
                              </span>{' '}
                              <BpAnchor
                                className={'whitespace-nowrap'}
                                href={item.ad.search_screen_url}
                                target='_blank'
                              >
                                {t('components.detailsCell.SERP')}
                              </BpAnchor>
                              ,{' '}
                              <BpAnchor
                                className={'whitespace-nowrap'}
                                href={item.ad.link_screen_url}
                                target='_blank'
                              >
                                {t('components.detailsCell.landingPage')}
                              </BpAnchor>
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={cellClass}>{item.trademark}</TableCell>
                      <TableCell className={cellClass}>
                        <div className={'flex flex-col gap-[8px]'}>
                          <div className={'flex flex-col gap-[4px]'}>
                            <ComplaintStatusBadge status={item.complaint_status} />
                            {(item.complaint_status === 'rejected' ||
                              item.complaint_status === 'disappeared') && (
                              <RejectionReasonLabel
                                disappeared={item.complaint_status === 'disappeared'}
                                replyType={item.reply_type}
                              />
                            )}
                          </div>
                          {item.has_reply && (
                            <button
                              className={'flex items-center gap-[4px]'}
                              onClick={() =>
                                dispatch(
                                  setModal({
                                    children: (
                                      <TrademarkInfringementsReplyModal
                                        caseId={item.id}
                                        projectId={Number(project)}
                                      />
                                    ),
                                    size: 'lg',
                                  }),
                                )
                              }
                            >
                              <div className={'w-[16px]'}>
                                <IconCross color={'#0E63D8'} />
                              </div>
                              <span
                                className={
                                  'text-left text-focus underline decoration-focus decoration-dotted'
                                }
                              >
                                {t('pages.trademarkInfringements.viewReply')}
                              </span>
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cellClass}>
                        <Tooltip title={item.actioned_by}>
                          <span className={'block w-fit max-w-[150px] truncate'}>
                            {item.actioned_by || '—'}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={cellClass}>{item.last_found}</TableCell>
                      <TableCell className={cellClass}>
                        {item.updated_at || (
                          <Tooltip title={t('pages.trademarkInfringements.updatedPendingTooltip')}>
                            <span className={'text-[13px] text-[#6684A7]'}>—</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </tr>
                  ))}
                </Table>
              </div>
            ),
          }[tab]
        }
      </div>
    </div>
  )
}
